import clsx from 'clsx'
import type { CommentHubMyComment, CommentHubMyCommentsSort } from 'shared-code/commenthub-api'
import { timeAgo } from '../comments/comment-format'
import { commentAnchorHref, ownSiteHref } from '../comments/notification-copy'
import { useDarkAccent } from './accent'
import { YOUR_COMMENTS_PAGE_SIZE, useYourComments, type YourCommentsState } from './useYourComments'
import styles from './YourComments.module.css'

/**
 * "Your comments" (plan §7, both brands): the signed-in reader's own comments under the Profile
 * cards (and in SoundGuys' member hub, owner layer only). Private by construction — the hook
 * makes no request without a session token and the first render is empty, so server HTML never
 * carries it. An older commentHub (404 on the route) draws no section at all.
 */

export const YOUR_COMMENTS_SORTS: readonly { id: CommentHubMyCommentsSort; label: string }[] = [
  { id: 'newest', label: 'Newest' },
  { id: 'top', label: 'Most liked' },
  { id: 'replies', label: 'With replies' },
]

export const YOUR_COMMENTS_EMPTY = 'No comments yet. Your comments on articles show here.'

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** "42 on 18 articles". */
export function yourCommentsSummary(total: number, postCount: number): string {
  return `${total} on ${plural(postCount, 'article', 'articles')}`
}

/**
 * The row's chip, at most one. A removed or held comment wears only its status: commentHub keeps
 * `featured` / `pinned` from before a removal (PR #35), so those flags count for a visible
 * comment only. None for a plain visible comment.
 */
export function commentStatusChips(
  comment: CommentHubMyComment
): { label: string; tone: 'accent' | 'held' | 'removed' }[] {
  if (comment.status === 'removed') {
    return [{ label: 'Removed', tone: 'removed' }]
  }
  if (comment.status === 'held') {
    return [{ label: 'Held for review', tone: 'held' }]
  }
  const chips: { label: string; tone: 'accent' | 'held' | 'removed' }[] = []
  // One word for one idea (2026-10-29): an editor's featured pick wears "Pinned" too, once.
  if (comment.featured || comment.pinned) {
    chips.push({ label: 'Pinned', tone: 'accent' })
  }
  return chips
}

/**
 * The row's links. The article: only on this app's own host or root-relative (`ownSiteHref`, the
 * site-event rule) — a row from another site, or an unsafe URL, prints its title unlinked. The
 * thread (replies, "Open in the thread"): `#conv-comment-<id>`, as the notification rows — never
 * for a removed comment, which has no place in the thread to open.
 */
export function yourCommentLinks(
  comment: CommentHubMyComment,
  appUrl: string | null
): { post: string | null; thread: string | null } {
  const post = comment.post.url ? ownSiteHref(comment.post.url, appUrl) : null
  const thread = post && comment.status !== 'removed' ? commentAnchorHref(post, comment.id) : null
  return { post, thread }
}

const YourCommentRow: React.VFC<{ comment: CommentHubMyComment; appUrl: string | null }> = ({
  comment,
  appUrl,
}) => {
  const links = yourCommentLinks(comment, appUrl)
  const replies = plural(comment.replyCount, 'reply', 'replies')
  return (
    <li className={styles.row}>
      <div className={styles.rowHead}>
        {links.post ? (
          <a className={styles.post} href={links.post}>
            {comment.post.title}
          </a>
        ) : (
          <span className={styles.post}>{comment.post.title}</span>
        )}
        {commentStatusChips(comment).map(chip => (
          <span key={chip.label} className={clsx(styles.status, styles[chip.tone])}>
            {chip.label}
          </span>
        ))}
        <time className={styles.when} dateTime={comment.createdAt}>
          {timeAgo(comment.createdAt)}
        </time>
      </div>
      <p className={clsx(styles.body, comment.status === 'removed' && styles.bodyRemoved)}>
        {comment.body}
      </p>
      <div className={styles.meta}>
        <span>{plural(comment.upvotes, 'like', 'likes')}</span>
        {links.thread && comment.replyCount > 0 ? (
          <a className={styles.replies} href={links.thread}>
            {replies}
          </a>
        ) : (
          <span>{replies}</span>
        )}
        {links.thread && (
          <a className={styles.open} href={links.thread}>
            {'Open in the thread ›'}
          </a>
        )}
      </div>
    </li>
  )
}

export interface YourCommentsViewProps {
  state: YourCommentsState
  appUrl: string | null
  onSort: (sort: CommentHubMyCommentsSort) => void
  onMore: () => void
  onRetry: () => void
  className?: string
}

/** The section as a pure view of the hook's state (specs render it per state). */
export const YourCommentsView: React.VFC<YourCommentsViewProps> = ({
  state,
  appUrl,
  onSort,
  onMore,
  onRetry,
  className,
}) => {
  const darkAccent = useDarkAccent()
  const { phase, total } = state
  let moreLabel = `Show ${YOUR_COMMENTS_PAGE_SIZE} more`
  if (state.more === 'loading') {
    moreLabel = 'Loading…'
  } else if (state.more === 'failed') {
    moreLabel = 'Try again'
  }
  // Nothing until the route has answered once: no session, an older commentHub, or the first
  // read in flight. A first read that FAILED says so (an account page never shows a loading
  // line for a failed read — becd13ede).
  if (phase === 'idle' || phase === 'absent' || (total === null && phase !== 'failed')) {
    return null
  }
  return (
    <section
      className={clsx(styles.section, darkAccent && styles.darkAccent, className)}
      aria-labelledby="account-your-comments"
    >
      <div className={styles.head}>
        <h2 className={styles.title} id="account-your-comments">
          {'Your comments'}
        </h2>
        {total !== null && (
          <span className={styles.summary}>{yourCommentsSummary(total, state.postCount)}</span>
        )}
        {total !== null && total > 0 && (
          <div className={styles.sorts} role="group" aria-label="Sort your comments">
            {YOUR_COMMENTS_SORTS.map(s => (
              <button
                key={s.id}
                type="button"
                className={clsx(styles.chip, s.id === state.sort && styles.chipOn)}
                aria-pressed={s.id === state.sort}
                onClick={() => onSort(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {state.items.length > 0 && (
        <ul className={styles.list}>
          {state.items.map(comment => (
            <YourCommentRow key={comment.id} comment={comment} appUrl={appUrl} />
          ))}
        </ul>
      )}

      {phase === 'loading' && <p className={styles.line}>{'Loading…'}</p>}

      {phase === 'failed' && (
        <div className={styles.line}>
          <p>{"Couldn't load your comments."}</p>
          <button type="button" className={styles.more} onClick={onRetry}>
            {'Try again'}
          </button>
        </div>
      )}

      {phase === 'ready' && state.items.length === 0 && (
        <p className={styles.line}>{YOUR_COMMENTS_EMPTY}</p>
      )}

      {phase === 'ready' && state.nextCursor && (
        <div className={styles.foot}>
          {state.more === 'failed' && <p className={styles.moreFailed}>{"Couldn't load more."}</p>}
          <button
            type="button"
            className={styles.more}
            disabled={state.more === 'loading'}
            onClick={onMore}
          >
            {moreLabel}
          </button>
        </div>
      )}
    </section>
  )
}

/** The section, wired: renders nothing for a signed-out reader and in server HTML. */
export const YourComments: React.VFC<{ className?: string }> = ({ className }) => {
  const { state, appUrl, setSort, showMore, retry } = useYourComments()
  return (
    <YourCommentsView
      state={state}
      appUrl={appUrl}
      onSort={setSort}
      onMore={showMore}
      onRetry={retry}
      className={className}
    />
  )
}

export default YourComments
